import React, { memo, useCallback, useEffect, useState, Fragment } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard, BtnGold, BtnGhost } from '../components/MarketplaceAtoms.js';
import { listingThumbUrlFromRow } from '../../lib/listingsService.js';
import { supabase } from '../../lib/supabase.js';
import {
  fetchPartnerPickupPackages,
  fetchListingRowForPickupPreview,
  insertPartnerPickupPackage,
  rpcPartnerPickupConfirmReceipt,
  rpcPartnerPickupConfirmDelivery,
  rpcPartnerPickupSetNotes,
  fetchPickupPackageEvents,
  partnerPickupRpcUserMessage,
} from '../../lib/partnerPickupService.js';
import { PARTNER_PICKUP_STATUS_LABEL_ES, PARTNER_PICKUP_EVENT_LABEL_ES } from '../constants/partnerPickup.js';
import SectionPartnerLogistics from '../components/SectionPartnerLogistics.js';
import PartnerSlaBanner from '../components/PartnerSlaBanner.js';
import TransactionShipmentPanel from '../components/TransactionShipmentPanel.js';
import { mergeTxLogisticsForPanel } from '../constants/transactionLogistics.js';

function listingFromPackageRow(row) {
  const L = row?.listing ?? row?.listings;
  const listing = Array.isArray(L) ? L[0] : L;
  return listing || null;
}

function sellerUsernameFromListing(listing) {
  const prof = listing?.profiles;
  const p = Array.isArray(prof) ? prof[0] : prof;
  return p?.username ?? null;
}

function formatEventDetail(ev) {
  try {
    const d = ev?.detail;
    if (!d || typeof d !== 'object') return '';
    if (ev.event_type === 'estado') {
      const de = d.de ?? '';
      const a = d.a ?? '';
      return `${PARTNER_PICKUP_STATUS_LABEL_ES[de] || de} → ${PARTNER_PICKUP_STATUS_LABEL_ES[a] || a}`;
    }
    if (ev.event_type === 'creado') {
      return `Estado: ${PARTNER_PICKUP_STATUS_LABEL_ES[d.status] || d.status || '—'}`;
    }
    return JSON.stringify(d);
  } catch {
    return '';
  }
}

const SectionPickupPartner = memo(function SectionPickupPartner({
  partner,
  pickupRoleRow,
  onError,
  onNotifySuccess,
  refetchRoles,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [shipmentPanelRowId, setShipmentPanelRowId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newListingId, setNewListingId] = useState('');
  const [newTxId, setNewTxId] = useState('');
  const [newBuyerId, setNewBuyerId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptCode, setReceiptCode] = useState('');
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [deliveryCode, setDeliveryCode] = useState('');
  const [notesModal, setNotesModal] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [availSaving, setAvailSaving] = useState(false);

  const pickupAvailable =
    pickupRoleRow?.role_data && typeof pickupRoleRow.role_data === 'object'
      ? pickupRoleRow.role_data.pickup_point_available !== false
      : true;

  const load = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    onError('');
    const { data, error } = await fetchPartnerPickupPackages(partner.id);
    if (error) {
      onError(error);
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [partner?.id, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async () => {
    setPreviewErr('');
    setPreview(null);
    const id = newListingId.trim();
    if (!/^[0-9a-f-]{36}$/i.test(id)) {
      setPreviewErr('Introduce un UUID de anuncio válido.');
      return;
    }
    const { row, error } = await fetchListingRowForPickupPreview(id);
    if (error) {
      setPreviewErr(error);
      return;
    }
    if (!row?.id) {
      setPreviewErr('No se encontró ese anuncio o no tienes permiso para verlo.');
      return;
    }
    setPreview(row);
  };

  const submitNew = async () => {
    if (!partner?.id) return;
    const id = newListingId.trim();
    if (!preview?.id || id !== preview.id) {
      onError('Carga y revisa el anuncio antes de registrar.');
      return;
    }
    const tx = newTxId.trim();
    const buyer = newBuyerId.trim();
    if (tx && !/^[0-9a-f-]{36}$/i.test(tx)) {
      onError('UUID de transacción no válido.');
      return;
    }
    if (buyer && !/^[0-9a-f-]{36}$/i.test(buyer)) {
      onError('UUID de comprador no válido.');
      return;
    }
    onError('');
    const { error } = await insertPartnerPickupPackage({
      partnerId: partner.id,
      listingId: preview.id,
      transactionId: tx || null,
      buyerProfileId: buyer || null,
    });
    if (error) {
      onError(error);
      return;
    }
    onNotifySuccess?.('Paquete registrado (asignado). Revisa el código de recepción en la tabla.');
    setModalOpen(false);
    setNewListingId('');
    setNewTxId('');
    setNewBuyerId('');
    setPreview(null);
    await load();
  };

  const setAvailability = async nextAvailable => {
    if (!pickupRoleRow?.id || !partner?.id) return;
    setAvailSaving(true);
    onError('');
    const prev = pickupRoleRow.role_data && typeof pickupRoleRow.role_data === 'object' ? pickupRoleRow.role_data : {};
    const role_data = { ...prev, pickup_point_available: nextAvailable };
    const { error } = await supabase
      .from('partner_roles')
      .update({ role_data })
      .eq('id', pickupRoleRow.id)
      .eq('partner_id', partner.id);
    setAvailSaving(false);
    if (error) {
      onError(error.message);
      return;
    }
    onNotifySuccess?.(nextAvailable ? 'Punto marcado como disponible.' : 'Punto marcado como no disponible.');
    await refetchRoles?.();
  };

  const openHistory = async row => {
    setHistoryModal(row);
    setHistoryRows([]);
    setHistoryLoading(true);
    const { data, error } = await fetchPickupPackageEvents(row.id);
    setHistoryLoading(false);
    if (error) {
      onError(error);
      setHistoryModal(null);
      return;
    }
    setHistoryRows(data ?? []);
  };

  const confirmReceipt = async () => {
    if (!receiptModal?.id) return;
    setBusyId(receiptModal.id);
    onError('');
    const { data, error } = await rpcPartnerPickupConfirmReceipt(receiptModal.id, receiptCode);
    setBusyId(null);
    if (error) {
      onError(error);
      return;
    }
    if (!data?.ok) {
      onError(partnerPickupRpcUserMessage(data?.error));
      return;
    }
    const ce = data.codigo_entrega;
    onNotifySuccess?.(
      ce
        ? `Recepción confirmada. Código de entrega para el comprador: ${ce}`
        : 'Recepción confirmada.'
    );
    setReceiptModal(null);
    setReceiptCode('');
    await load();
  };

  const confirmDelivery = async () => {
    if (!deliveryModal?.id) return;
    setBusyId(deliveryModal.id);
    onError('');
    const { data, error } = await rpcPartnerPickupConfirmDelivery(deliveryModal.id, deliveryCode);
    setBusyId(null);
    if (error) {
      onError(error);
      return;
    }
    if (!data?.ok) {
      onError(partnerPickupRpcUserMessage(data?.error));
      return;
    }
    onNotifySuccess?.('Entrega confirmada.');
    setDeliveryModal(null);
    setDeliveryCode('');
    await load();
  };

  const saveNotes = async () => {
    if (!notesModal?.id) return;
    setBusyId(notesModal.id);
    onError('');
    const { data, error } = await rpcPartnerPickupSetNotes(notesModal.id, notesDraft);
    setBusyId(null);
    if (error) {
      onError(error);
      return;
    }
    if (!data?.ok) {
      onError(partnerPickupRpcUserMessage(data?.error));
      return;
    }
    onNotifySuccess?.('Notas guardadas.');
    setNotesModal(null);
    await load();
  };

  return (
    <div>
      <SectionPartnerLogistics
        role="pickup"
        roleRow={pickupRoleRow}
        onError={onError}
        onNotifySuccess={onNotifySuccess}
      />
      <PartnerSlaBanner role="pickup" />
      <DCard style={{ padding: 18, marginBottom: 16, background: 'rgba(200,168,75,0.06)', borderColor: 'rgba(200,168,75,0.22)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, color: C.gold }}>Punto DreamVinyl (operativo)</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
          Flujo: <strong style={{ color: '#fff' }}>Asignado → Recibido (código recepción) → Entregado (código entrega)</strong>. El alta exige anuncio activo
          con recogida en punto o inventario vendido por tu cuenta partner. Un anuncio no puede tener dos paquetes abiertos en el mismo punto.
        </p>
        <div
          style={{
            marginTop: 14,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>Disponibilidad del punto</span>
            <button
              type="button"
              disabled={availSaving || !pickupRoleRow?.id}
              onClick={() => void setAvailability(!pickupAvailable)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: `0.5px solid ${pickupAvailable ? C.green : 'rgba(255,255,255,0.2)'}`,
                background: pickupAvailable ? 'rgba(29,158,117,0.18)' : 'rgba(0,0,0,0.35)',
                color: pickupAvailable ? C.green : 'rgba(255,255,255,0.55)',
                fontSize: 12,
                fontWeight: 700,
                cursor: pickupRoleRow?.id ? 'pointer' : 'not-allowed',
              }}
            >
              {availSaving ? 'Guardando…' : pickupAvailable ? 'Disponible' : 'No disponible'}
            </button>
          </div>
          <BtnGold type="button" style={{ fontSize: 12 }} onClick={() => setModalOpen(true)}>
            + Nuevo paquete
          </BtnGold>
          <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => load()} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar tabla'}
          </BtnGhost>
        </div>
      </DCard>

      {modalOpen ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(255,255,255,0.12)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Registrar paquete en el punto</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
            Pega el UUID del anuncio. Opcionalmente transacción y perfil comprador. Comprueba la vista previa y confirma.
          </p>
          <input
            value={newListingId}
            onChange={e => setNewListingId(e.target.value)}
            placeholder="UUID del anuncio"
            style={{
              width: '100%',
              maxWidth: 420,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              marginBottom: 10,
              boxSizing: 'border-box',
            }}
          />
          <input
            value={newTxId}
            onChange={e => setNewTxId(e.target.value)}
            placeholder="UUID transacción (opcional)"
            style={{
              width: '100%',
              maxWidth: 420,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              marginBottom: 10,
              boxSizing: 'border-box',
            }}
          />
          <input
            value={newBuyerId}
            onChange={e => setNewBuyerId(e.target.value)}
            placeholder="UUID perfil comprador (opcional)"
            style={{
              width: '100%',
              maxWidth: 420,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              marginBottom: 10,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => void openPreview()}>
              Cargar anuncio
            </BtnGhost>
          </div>
          {previewErr ? <div style={{ fontSize: 12, color: '#ffb4b4', marginBottom: 10 }}>{previewErr}</div> : null}
          {preview ? (
            <div style={{ padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.04)', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {preview.artist} — {preview.title}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                Estado anuncio: {preview.status || '—'} · Recogida en punto: {preview.ships_pickup ? 'sí' : 'no'}
              </div>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} onClick={() => void submitNew()} disabled={!preview}>
              Registrar como asignado
            </BtnGold>
            <BtnGhost
              type="button"
              style={{ fontSize: 12 }}
              onClick={() => {
                setModalOpen(false);
                setPreview(null);
                setPreviewErr('');
                setNewTxId('');
                setNewBuyerId('');
              }}
            >
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      {receiptModal ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(200,168,75,0.25)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Confirmar recepción física</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
            Introduce el código de recepción del paquete (visible en la tabla cuando está asignado).
          </p>
          <input
            value={receiptCode}
            onChange={e => setReceiptCode(e.target.value)}
            placeholder="Código recepción"
            autoComplete="off"
            style={{
              width: 200,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 14,
              letterSpacing: '0.08em',
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => void confirmReceipt()}>
              Confirmar recepción
            </BtnGold>
            <BtnGhost type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => { setReceiptModal(null); setReceiptCode(''); }}>
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      {deliveryModal ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(200,168,75,0.25)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Confirmar entrega al comprador</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
            El comprador debe facilitarte el código de entrega generado al confirmar la recepción.
          </p>
          <input
            value={deliveryCode}
            onChange={e => setDeliveryCode(e.target.value)}
            placeholder="Código entrega"
            autoComplete="off"
            style={{
              width: 200,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 14,
              letterSpacing: '0.08em',
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => void confirmDelivery()}>
              Confirmar entrega
            </BtnGold>
            <BtnGhost type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => { setDeliveryModal(null); setDeliveryCode(''); }}>
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      {notesModal ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(255,255,255,0.12)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Notas internas</div>
          <textarea
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              maxWidth: 480,
              padding: 10,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 12,
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => void saveNotes()}>
              Guardar
            </BtnGold>
            <BtnGhost type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => setNotesModal(null)}>
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      {historyModal ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(255,255,255,0.12)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Historial del paquete</div>
          {historyLoading ? (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
          ) : historyRows.length === 0 ? (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Sin eventos.</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
              {historyRows.map(ev => {
                const det = formatEventDetail(ev);
                return (
                  <li key={ev.id} style={{ marginBottom: 6 }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {ev.created_at ? new Date(ev.created_at).toLocaleString('es-ES') : ''}
                    </span>{' '}
                    — {PARTNER_PICKUP_EVENT_LABEL_ES[ev.event_type] || ev.event_type}
                    {det ? `: ${det}` : ''}
                  </li>
                );
              })}
            </ul>
          )}
          <BtnGhost type="button" style={{ fontSize: 12, marginTop: 12 }} onClick={() => setHistoryModal(null)}>
            Cerrar
          </BtnGhost>
        </DCard>
      ) : null}

      <DCard style={{ padding: 0, overflow: 'hidden', borderColor: 'rgba(255,255,255,0.1)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 920 }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.35)', color: 'rgba(255,255,255,0.55)', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Disco</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Vendedor</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Estado</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Códigos</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Notas</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Actualizado</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 22, color: 'rgba(255,255,255,0.45)' }}>
                    Cargando paquetes…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 22, color: 'rgba(255,255,255,0.45)' }}>
                    No hay paquetes. Registra el primero con «Nuevo paquete».
                  </td>
                </tr>
              ) : (
                rows.map(row => {
                  const listing = listingFromPackageRow(row);
                  const thumb = listing ? listingThumbUrlFromRow(listing) : null;
                  const sellerUser = sellerUsernameFromListing(listing);
                  return (
                    <Fragment key={row.id}>
                    <tr style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              flexShrink: 0,
                              background: thumb ? `url(${thumb}) center/cover` : 'rgba(255,255,255,0.06)',
                            }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: '#fff', fontWeight: 600, lineHeight: 1.35 }}>
                              {listing ? `${listing.artist} — ${listing.title}` : '—'}
                            </div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>{listing?.id || row.listing_id}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.75)' }}>
                        {sellerUser ? `@${sellerUser}` : listing?.seller_id ? String(listing.seller_id).slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 999,
                            background: 'rgba(200,168,75,0.15)',
                            border: `0.5px solid ${C.gold}`,
                            color: C.gold,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          {PARTNER_PICKUP_STATUS_LABEL_ES[row.status] || row.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.65)', fontSize: 11, lineHeight: 1.45 }}>
                        {row.status === 'asignado' && row.codigo_recepcion ? (
                          <div>
                            Recepción: <strong style={{ color: '#fff' }}>{row.codigo_recepcion}</strong>
                          </div>
                        ) : null}
                        {row.status === 'recibido' && row.codigo_entrega ? (
                          <div style={{ marginTop: 4 }}>
                            Entrega: <strong style={{ color: C.green }}>{row.codigo_entrega}</strong>
                          </div>
                        ) : null}
                        {row.status === 'entregado' ? <div style={{ color: 'rgba(255,255,255,0.35)' }}>Cerrado</div> : null}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.55)', maxWidth: 160 }}>
                        {row.partner_notes ? String(row.partner_notes).slice(0, 120) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                        {row.updated_at ? new Date(row.updated_at).toLocaleString('es-ES') : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                          {row.status === 'asignado' ? (
                            <BtnGold
                              type="button"
                              style={{ fontSize: 11, padding: '6px 12px' }}
                              disabled={busyId === row.id}
                              onClick={() => {
                                setReceiptModal(row);
                                setReceiptCode('');
                              }}
                            >
                              Confirmar recepción
                            </BtnGold>
                          ) : null}
                          {row.status === 'recibido' ? (
                            <BtnGold
                              type="button"
                              style={{ fontSize: 11, padding: '6px 12px' }}
                              disabled={busyId === row.id}
                              onClick={() => {
                                setDeliveryModal(row);
                                setDeliveryCode('');
                              }}
                            >
                              Confirmar entrega
                            </BtnGold>
                          ) : null}
                          <BtnGhost
                            type="button"
                            style={{ fontSize: 11, padding: '6px 12px' }}
                            disabled={busyId === row.id}
                            onClick={() => {
                              setNotesModal(row);
                              setNotesDraft(row.partner_notes ?? '');
                            }}
                          >
                            Notas
                          </BtnGhost>
                          <BtnGhost type="button" style={{ fontSize: 11, padding: '6px 12px' }} onClick={() => void openHistory(row)}>
                            Historial
                          </BtnGhost>
                          {row.transaction_id ? (
                            <BtnGhost
                              type="button"
                              style={{ fontSize: 11, padding: '6px 12px' }}
                              onClick={() => setShipmentPanelRowId(id => (id === row.id ? null : row.id))}
                            >
                              {shipmentPanelRowId === row.id ? 'Ocultar envío' : 'Envío / mensajes'}
                            </BtnGhost>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {shipmentPanelRowId === row.id && row.transaction_id ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 12px 14px', background: 'rgba(0,0,0,0.2)' }}>
                          <TransactionShipmentPanel
                            transaction={mergeTxLogisticsForPanel(row)}
                            viewerRole="partner"
                            currentUserId={partner?.profile_id}
                            onUpdated={() => void load()}
                            title="Logística y mensajes de la venta"
                          />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </DCard>
    </div>
  );
});

export default SectionPickupPartner;
